import { PlusOutlined } from "@ant-design/icons";
import { Box, styled } from "@mui/material";
import SpinFC from "antd/es/spin";
import { useEffect, useRef, useState } from "react";
import Dropzone, { FileWithPath } from "react-dropzone";
import { FaTrashAlt } from "react-icons/fa";
import { ReactSortable } from "react-sortablejs";

import { toast } from "react-toastify";
import api from "../../../hooks/api";
import { pathAPI } from "../../../utils/pathApi";
import { ImageItem } from "../../../utils/types";

interface UploadMultiImageProps {
  setListImage?: (files: ImageItem[]) => void;
  listImage?: ImageItem[];
  loadingProps?: boolean;
  disabled?: boolean;
  max?: number;
  /** Number of slots per row (grid is `max / columns` rows) */
  columns?: number;
  /** Min width (px) of a slot: slots grow to fill the row and wrap onto more rows (overrides `columns`) */
  slotSize?: number;
  /** Awaiting approval: new URL by the approved URL it will replace (shown in that slot, locked) */
  pendingReplacements?: Record<string, string>;
  /** Awaiting approval: new photos shown after the approved ones (they use up slots) */
  pendingAdded?: string[];
}

const sameImages = (a: ImageItem[], b: ImageItem[]) =>
  a === b || (a.length === b.length && a.every((item, i) => item.id === b[i].id && item.url === b[i].url));

const MAX_FILE_SIZE_MB = 5;
const GRID_GAP = 12;
const SLOT_RADIUS = "8px";

const UploadMultiImage = ({
  setListImage,
  listImage = [],
  loadingProps = false,
  disabled = false,
  max = 10,
  columns = 5,
  slotSize,
  pendingReplacements = {},
  pendingAdded = [],
}: UploadMultiImageProps) => {
  const [selectedFiles, setSelectedFiles] = useState<ImageItem[]>(listImage);
  const [loading, setLoading] = useState(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    setSelectedFiles((prev) => (sameImages(prev, listImage) ? prev : listImage));
    initializedRef.current = true;
  }, [listImage]);

  useEffect(() => {
    // Only report real changes: echoing an equal list back makes the parent
    // hand over a new array, which re-renders (and flickers) the whole grid.
    if (initializedRef.current && !sameImages(selectedFiles, listImage)) {
      setListImage?.(selectedFiles);
    }
  }, [selectedFiles]);

  const handleDrop = async (acceptedFiles: FileWithPath[]) => {
    const validFiles = acceptedFiles.filter(
      (file) => file.size <= MAX_FILE_SIZE_MB * 1024 * 1024
    );
    if (validFiles.length !== acceptedFiles.length) {
      toast.error(`Một hoặc nhiều file vượt quá ${MAX_FILE_SIZE_MB}MB và đã bị loại bỏ.`);
    }
    if (validFiles.length === 0) return;

    setLoading(true);
    try {
      const formData = new FormData();
      validFiles.forEach((file) => formData.append("files", file));

      const response = await api.POST<{ data: { url: string }[] }>(
        pathAPI.uploadMultiImage.replace(":type", "IMAGE"),
        formData,
        {},
        true
      );

      // The URL is unique, so it doubles as a stable id for sorting
      const newItems: ImageItem[] =
        response?.data?.map((i: { url: string }) => ({ id: i.url, url: i.url })) || [];

      setSelectedFiles((prev) => [...prev, ...newItems].slice(0, max));
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const emptySlots = Math.max(0, max - selectedFiles.length - pendingAdded.length);
  const canUpload = !disabled;
  const isBusy = loading || loadingProps;

  return (
    <Grid columns={columns} slotSize={slotSize}>
      {/* `display: contents` keeps the sortable wrapper out of the grid layout */}
      <ReactSortable
        list={selectedFiles}
        setList={setSelectedFiles}
        animation={200}
        handle=".draggable"
        style={{ display: "contents" }}
      >
        {selectedFiles.map((item, index) => {
          const pendingUrl = pendingReplacements[item.url];
          return (
            <Slot key={item.id} className="draggable" data-id={item.id}>
              <img
                src={pendingUrl ?? item.url}
                alt={`Preview ${index}`}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              {pendingUrl && <PendingBadge>Pending</PendingBadge>}
              {!disabled && !pendingUrl && (
                <IconDelete
                  onClick={() => handleRemove(index)}
                  className="iconDelete"
                  style={{ cursor: "pointer" }}
                >
                  <FaTrashAlt fontSize={16} style={{ color: "white" }} />
                </IconDelete>
              )}
            </Slot>
          );
        })}
      </ReactSortable>

      {pendingAdded.map((url) => (
        <Slot key={`pending-${url}`}>
          <img src={url} alt="Pending" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <PendingBadge>Pending</PendingBadge>
        </Slot>
      ))}

      {Array.from({ length: emptySlots }, (_, i) => (
        <Slot key={`empty-${i}`} dashed>
          {isBusy ? (
            <SpinFC />
          ) : canUpload ? (
            <Dropzone onDrop={handleDrop} multiple maxFiles={emptySlots}>
              {({ getRootProps, getInputProps }) => (
                <UploadTile {...getRootProps()}>
                  <input {...getInputProps()} />
                  <PlusOutlined />
                </UploadTile>
              )}
            </Dropzone>
          ) : null}
        </Slot>
      ))}
    </Grid>
  );
};

export default UploadMultiImage;

// Styled Components
const Grid = styled(Box, {
  shouldForwardProp: (p) => p !== "columns" && p !== "slotSize",
})<{ columns: number; slotSize?: number }>(
  ({ columns, slotSize }) => ({
    display: "grid",
    gridTemplateColumns: slotSize
      ? `repeat(auto-fill, minmax(${slotSize}px, 1fr))`
      : `repeat(${columns}, minmax(0, 1fr))`,
    gap: GRID_GAP,
  })
);

const Slot = styled(Box, { shouldForwardProp: (p) => p !== "dashed" })<{ dashed?: boolean }>(
  ({ dashed }) => ({
    position: "relative",
    aspectRatio: "1 / 1",
    overflow: "hidden",
    borderRadius: SLOT_RADIUS,
    border: dashed ? "1px dashed var(--app-border)" : "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ":hover .iconDelete": { display: "block" },
  })
);

const UploadTile = styled("div")({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  fontSize: 20,
  color: "var(--app-text-muted)",
  ":hover": { color: "#ba2156" },
});

const PendingBadge = styled("span")({
  position: "absolute",
  left: 6,
  bottom: 6,
  padding: "2px 8px",
  borderRadius: 10,
  fontSize: 11,
  color: "#fff",
  background: "#ed6c02",
});

const IconDelete = styled(Box)({
  position: "absolute",
  top: 0,
  right: 0,
  display: "none",
  padding: 8,
  background: "rgba(0,0,0,0.45)",
  borderBottomLeftRadius: SLOT_RADIUS,
});
